import React, { useEffect, useState } from 'react';
import { Package, Tag, Layers, Box, TrendingUp, RotateCcw, Search, ScanBarcode, Loader2, Lock, AlertTriangle, PackageMinus, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import SectionDashboard from '../SectionDashboard';
import { moduleReportTiles, fobPackedNotTransferredTile } from '@/lib/sectionModules';
import { Switch } from '@/components/ui/switch';
import { getFobSettings, updateFobSettings } from '@/services/fobSectionApi';

const isAdminUser = () => {
  try {
    const role = (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase();
    return ['admin', 'site admin', 'super_admin'].includes(role);
  } catch {
    return false;
  }
};

const PrepastedModeBanner = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getFobSettings();
        setEnabled(!!data.prepasted_usn_mode);
      } catch {
        /* non-blocking */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (next) => {
    setSaving(true);
    try {
      await updateFobSettings(next);
      setEnabled(next);
      toast.success(
        next
          ? 'Pre-pasted USN mode ON — new documents skip auto-assign; scan pre-printed stickers in Assortment Packing.'
          : 'Pre-pasted USN mode OFF — back to auto-assign + print USN stickers.'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border-2 border-[#2D5F4F]/30 bg-white px-5 py-4 flex items-center justify-between gap-4 shadow-sm"
      data-testid="fob-prepasted-mode-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-[#2D5F4F] flex items-center justify-center flex-shrink-0">
          <ScanBarcode className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-[#1A1F2E] font-display">Pre-pasted USN Mode</h3>
          <p className="text-sm text-[#5A5F6E] max-w-2xl">
            When ON, Sort No assignment only records sort no + qty (no USN auto-assign or printing).
            Operators scan the pre-printed stickers in Assortment Packing, tagging them to the sort no + bundle.
            Already-assigned documents keep the old flow.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {loading || saving ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#2D5F4F]" />
        ) : (
          <span className={`text-sm font-semibold ${enabled ? 'text-[#2D5F4F]' : 'text-[#5A5F6E]'}`}>
            {enabled ? 'ON' : 'OFF'}
          </span>
        )}
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={loading || saving}
          data-testid="fob-prepasted-mode-switch"
        />
      </div>
    </div>
  );
};

const FOBDashboard = () => {
  const isAdmin = isAdminUser();

  // FOB-specific operations (4 operations)
  const operations = [
    {
      id: 'bundle-receipt',
      title: 'Bundle Receipt Entry',
      description: 'Verify and receive bundles from inward',
      icon: Package,
      route: '/sections/fob/bundle-receipt',
      color: 'bg-blue-500'
    },
    {
      id: 'usn-tagging',
      title: 'Sort No → USN Tagging',
      description: 'Tag and print USN labels',
      icon: Tag,
      route: '/sections/fob/usn-tagging',
      color: 'bg-purple-500'
    },
    {
      id: 'assortment-packing',
      title: 'Assortment Packing',
      description: 'Combined multi-staff assortment & packing into bundles',
      icon: Box,
      route: '/sections/fob/assortment-packing',
      color: 'bg-orange-500'
    }
  ];

  // FOB-specific utilities
  const utilities = [
    {
      id: 'supplier-returns',
      title: 'Supplier Returns',
      description: 'Send back goods received without a PO or above the PO — approve and print the Return Challan',
      icon: PackageMinus,
      route: '/sections/fob/supplier-returns',
      color: 'bg-amber-700',
      testId: 'supplier-returns-tile'
    },
    {
      id: 'grn',
      title: 'Goods Receipt Notes',
      description: 'Finalise the GRN of a supplier delivery once its bundles are counted on the PO',
      icon: ClipboardCheck,
      route: '/po/grn',
      color: 'bg-emerald-700',
      testId: 'grn-tile'
    },
    {
      id: 'staff-performance',
      title: 'Staff Performance',
      description: 'View productivity and work metrics',
      icon: TrendingUp,
      route: '/sections/fob/staff-performance',
      color: 'bg-teal-500'
    },
    {
      id: 'usn-reprint',
      title: 'USN Reprint',
      description: 'Reprint damaged or missing USN stickers',
      icon: RotateCcw,
      route: '/sections/fob/usn-reprint',
      color: 'bg-indigo-500'
    },
    {
      id: 'usn-report',
      title: 'USN Status Report',
      description: 'Search by USN, Bundle, Document, Sort No — view Tagged / Assorted / Packed / Shipped status',
      icon: Search,
      route: '/sections/fob/usn-report',
      color: 'bg-slate-600'
    },
    {
      id: 'damage-report',
      title: 'Damage Pieces Report',
      description: 'Damaged pieces by design / vendor with CSV export and return-to-supplier',
      icon: AlertTriangle,
      route: '/sections/fob/damage-report',
      color: 'bg-red-600'
    }
  ];

  if (isAdmin) {
    utilities.push({
      id: 'force-close',
      title: 'Force-Close Documents',
      description: 'Close short/excess pre-pasted documents (logs shortage/excess) and re-open later',
      icon: Lock,
      route: '/sections/fob/force-close',
      color: 'bg-amber-600'
    });
  }

  return (
    <SectionDashboard
      sectionName="FOB Section"
      sectionId="fob"
      sectionKey="FOB"
      description="Operations Dashboard"
      backRoute="/dashboard"
      operations={operations}
      utilities={[...utilities, ...moduleReportTiles('fob'), fobPackedNotTransferredTile()]}
      alertComponent={isAdmin ? <PrepastedModeBanner /> : null}
    />
  );
};

export default FOBDashboard;
